// ============================================================
// Diagnostics — hidden Sentry smoke-test surface.
//
// Mounted at /square/__diag inside the MainframeShell. ONLY
// renders content when ?debug=1 is in the query string. With
// no debug flag the route shows a single line "Diagnostics
// gated" so it cannot be discovered casually in production.
//
// Buttons fire the actual instrumented paths so you can verify
// in Sentry that:
//   - app_shell, module, mode, route, panel_mode, release tags
//     are present on every event
//   - hook_outcome=missing_dependency, realtime_failure, live_error
//     classify correctly
//   - the React error boundary catches an unhandled throw
//   - feedback dialog opens
//
// Also exposes window.__sqDiag for browser-console invocation.
// ============================================================

import { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import {
  captureHookFailure,
  isFeedbackAvailable,
  isInitialized,
  openFeedbackDialog,
  recordHookOutcome,
  recordShellAction,
} from './sentry'
import { useShell } from '../shell/ShellContext'
import { api, ApiError } from '../api/client'

type SentryHealth = Awaited<ReturnType<typeof api.health.sentry>>['sentry']

declare global {
  interface Window {
    __sqDiag?: ReturnType<typeof buildDiag>
  }
}

function buildDiag() {
  return {
    throwError: () => {
      throw new Error('sq-diag: deliberate render error')
    },
    captureSimulatedHookFailure: (kind: 'missing_dependency' | 'realtime_failure' | 'live_error' = 'missing_dependency') => {
      captureHookFailure('useChainIntegrity', kind, new Error(`sq-diag: simulated ${kind}`), {
        rpc: 'olv_verify_event_chain',
        message: `simulated ${kind} from diagnostics`,
      })
    },
    recordSuccess: () => {
      recordHookOutcome('useProjects', 'live_success', { rows: 7, durationMs: 12 })
    },
    openFeedback: () => openFeedbackDialog(),
    breadcrumb: (label: string) => recordShellAction(`diag.${label}`),
  }
}

export default function Diagnostics() {
  const { search } = useLocation()
  const { panelMode, openDetail } = useShell()
  const [thrown, setThrown] = useState(false)
  const [sentryReadiness, setSentryReadiness] = useState<SentryHealth | null>(null)
  const [readinessError, setReadinessError] = useState<string | null>(null)
  const [readinessLoading, setReadinessLoading] = useState(false)

  async function refreshReadiness() {
    setReadinessLoading(true)
    setReadinessError(null)
    try {
      const result = await api.health.sentry()
      setSentryReadiness(result.sentry)
    } catch (err) {
      const apiErr = err as ApiError
      setReadinessError(apiErr?.message || 'sentry_health_failed')
      setSentryReadiness(null)
    } finally {
      setReadinessLoading(false)
    }
  }

  // Expose globally for console use, even if route is gated.
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.__sqDiag = buildDiag()
    }
  }, [])

  // Hydrate the server-side Sentry readiness panel on mount.
  useEffect(() => {
    void refreshReadiness()
  }, [])

  // Throw inside render so the Sentry boundary catches it.
  if (thrown) throw new Error('sq-diag: deliberate render error')

  const params = new URLSearchParams(search)
  if (params.get('debug') !== '1') {
    return (
      <div style={{ padding: 24, color: '#7f8db4', fontFamily: 'var(--font-mono, monospace)', fontSize: 11 }}>
        Diagnostics gated. Append ?debug=1 to enable.
      </div>
    )
  }

  const initialized = isInitialized()
  const feedbackOk = isFeedbackAvailable()
  const diag = buildDiag()

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
      <div>
        <div
          style={{
            fontSize: 11,
            color: '#7f8db4',
            fontFamily: 'var(--font-mono)',
            letterSpacing: '0.16em',
            marginBottom: 6,
          }}
        >
          OBSERVABILITY · DIAGNOSTICS
        </div>
        <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>Sentry smoke test</h1>
        <div style={{ fontSize: 13, color: '#97a7cf', marginTop: 4 }}>
          Buttons fire real instrumentation paths. Verify each one in your
          Sentry project. Also available on <code>window.__sqDiag</code>.
        </div>
      </div>

      <Card>
        <Field label="Browser SDK initialized">{String(initialized)}</Field>
        <Field label="Feedback available">{String(feedbackOk)}</Field>
        <Field label="Current panel mode">{panelMode}</Field>
        <Field label="Browser release (VITE_APP_RELEASE)">
          <code>{(import.meta as any).env?.VITE_APP_RELEASE || '(unset)'}</code>
        </Field>
      </Card>

      <Card>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 4,
          }}
        >
          <span style={{ fontSize: 11, color: '#7f8db4', letterSpacing: '0.16em', fontFamily: 'var(--font-mono)' }}>
            SERVER · /api/health/sentry
          </span>
          <Btn onClick={refreshReadiness} disabled={readinessLoading}>
            {readinessLoading ? 'Loading…' : 'Refresh'}
          </Btn>
        </div>
        {readinessError ? (
          <div style={{ fontSize: 12, color: '#f3a3a3' }}>
            failed · {readinessError}
          </div>
        ) : sentryReadiness ? (
          <>
            <Field label="Server envelope ready">
              {String(sentryReadiness.ready)}
              {sentryReadiness.ingestion.server_envelope.via && (
                <span style={{ color: '#7f8db4', marginLeft: 6 }}>
                  via {sentryReadiness.ingestion.server_envelope.via}
                </span>
              )}
            </Field>
            <Field label="Browser DSN configured">
              {String(sentryReadiness.ingestion.browser_dsn.configured)}
            </Field>
            <Field label="Release management (auth_token)">
              {String(sentryReadiness.release_management.auth_token.configured)}
            </Field>
            <Field label="Sentry org">
              {String(sentryReadiness.release_management.org.configured)}
            </Field>
            <Field label="Sentry project">
              {String(sentryReadiness.release_management.project.configured)}
            </Field>
            <Field label="OTLP traces drain">
              {String(sentryReadiness.drains.otlp_traces.configured)}
            </Field>
            <Field label="Vercel log drain">
              {String(sentryReadiness.drains.vercel_log_drain.configured)}
            </Field>
            <Field label="Release SHA">
              <code>{sentryReadiness.release.sha || '(unset)'}</code>
            </Field>
            <Field label="Vercel env">
              <code>{sentryReadiness.environment || '(unset)'}</code>
            </Field>
          </>
        ) : (
          <div style={{ fontSize: 12, color: '#7f8db4' }}>hydrating…</div>
        )}
      </Card>

      <Card>
        <Row label="Render-time exception (caught by boundary)">
          <Btn onClick={() => setThrown(true)}>Throw</Btn>
        </Row>
        <Row label="Simulated hook failure: missing_dependency">
          <Btn onClick={() => diag.captureSimulatedHookFailure('missing_dependency')}>Capture</Btn>
        </Row>
        <Row label="Simulated hook failure: realtime_failure">
          <Btn onClick={() => diag.captureSimulatedHookFailure('realtime_failure')}>Capture</Btn>
        </Row>
        <Row label="Simulated hook failure: live_error">
          <Btn onClick={() => diag.captureSimulatedHookFailure('live_error')}>Capture</Btn>
        </Row>
        <Row label="Record live_success outcome (breadcrumb only)">
          <Btn onClick={diag.recordSuccess}>Record</Btn>
        </Row>
        <Row label="Open right panel in detail mode (sets selection context)">
          <Btn
            onClick={() =>
              openDetail({
                id: 'diag-1',
                action: 'diag.synthetic_event',
                actor_id: 'diag',
                severity: 'warning',
                event_hash: '0xdeadbeef',
              })
            }
          >
            Open detail
          </Btn>
        </Row>
        <Row label="Open feedback dialog">
          <Btn onClick={diag.openFeedback} disabled={!feedbackOk}>
            Open
          </Btn>
        </Row>
      </Card>
    </div>
  )
}

function Card({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        border: '1px solid rgba(255,255,255,0.08)',
        background: 'rgba(255,255,255,0.035)',
        borderRadius: 14,
        padding: 16,
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
      }}
    >
      {children}
    </div>
  )
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
      <span style={{ fontSize: 12, color: '#dbe5ff' }}>{label}</span>
      {children}
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
      <span style={{ fontSize: 11, color: '#8ea1cf', fontFamily: 'var(--font-mono)' }}>{label}</span>
      <span style={{ fontSize: 12 }}>{children}</span>
    </div>
  )
}

function Btn({ children, onClick, disabled }: { children: React.ReactNode; onClick: () => void; disabled?: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        border: '1px solid rgba(95,142,255,0.42)',
        background: disabled ? 'rgba(255,255,255,0.04)' : 'rgba(66,110,255,0.16)',
        color: '#edf2ff',
        borderRadius: 8,
        padding: '6px 12px',
        fontSize: 11,
        fontWeight: 700,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        fontFamily: 'inherit',
      }}
    >
      {children}
    </button>
  )
}
